// Import react elements
import { useState } from 'react';

// Import components
import CreateButton from "global/components/CreateButton";
import { ImageLoader } from "global/components/ImageLoader";



// Represents a single image with thumbnail, delete control, and selection logic
const ImageCard = ({ image, index, handleImageSelect, selected, onOpenChildren, childrenLoading = false, childrenNavigationDisabled = false, dragDisabled = false, allowParentDrop = false, onMoveImage, onDelete }) => {

    // Convert the child count to a number so the associated-file control only appears for real child records.
    const childCount = Number(image.child_count);
    const hasChildren = Number.isFinite(childCount) && childCount > 0;

    // Include the parent itself in the number of files shown when opening this group.
    const associatedFileCount = childCount + 1;

    // Check whether this bucket item can be shown as an image preview.
    const isImageFile = image.file_class === "image";

    // Track whether another item is currently being dragged over this card.
    const [dropTarget, setDropTarget] = useState(false);

    // Only unlocked leaf items can be moved underneath another bucket item.
    const canDrag = !dragDisabled && !childrenNavigationDisabled && image.locked !== "Y" && childCount === 0;

    // Start dragging this bucket item.
    const handleDragStart = (event) => {

        // Store the public bucket id so any supported drop target can use it.
        event.dataTransfer.setData('text/plain', String(image.id));
        event.dataTransfer.effectAllowed = 'move';

    };



    // Allow this root item to receive another leaf item as a child.
    const handleDragOver = (event) => {

        // Associated child cards cannot themselves become hierarchy drop targets.
        if (!allowParentDrop) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropTarget(true);

    };



    // Assign the dragged item to this bucket item.
    const handleDrop = (event) => {

        // Associated child cards cannot themselves become hierarchy drop targets.
        if (!allowParentDrop) return;

        event.preventDefault();
        setDropTarget(false);

        // Get the public id supplied by the dragged ImageCard.
        const imageId = event.dataTransfer.getData('text/plain');

        // Ignore empty drags and dropping a card onto itself.
        if (!imageId || String(imageId) === String(image.id)) return;

        // Set this root bucket item as the new parent.
        onMoveImage(imageId, image.id);

    };


    // Handle click event to select image
    const handleClick = (event) => {

        // Run function to handle image selection
        handleImageSelect(image.id, index, event);

    };



    return (



        <div
            className={`image-card ${selected ? 'image-card--selected' : ''} ${dropTarget ? 'image-card--drop-target' : ''}`}
            onMouseDown={handleClick}
            draggable={canDrag}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={() => setDropTarget(false)}
            onDrop={handleDrop}
        >

            {/* Show the image title at the top-left of the card. */}
            <div className="image-card__name" title={image.name}>
                {image.name}
            </div>

            {/* Every bucket item receives either its own thumbnail or a file-class placeholder from the API. */}
            <ImageLoader
                src={image.thumbnail_url || image.image_url}
                alt={image.name}
                className="image-card__thumbnail"
            />

            {/* Keep all card controls together underneath the image. */}
            <div
                className="image-card__actions"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
            >

                <div className="image-card__actions-left">

                    {/* Delete this bucket item directly from its card. */}
                    {image.locked !== "Y" &&

                        <CreateButton
                            icon="/images/icon_bin.png"
                            iconTitle="Delete"
                            tooltip="Delete image"
                            size="xs"
                            isDelete={true}
                            onClick={() => onDelete(image.id)}
                        />

                    }

                    {/* Show a folder-style control when this bucket item has associated child files. */}
                    {hasChildren && !childrenNavigationDisabled &&

                        <button
                            type="button"
                            className="image-card__children"
                            title={childrenLoading ? "Loading associated files..." : `View ${associatedFileCount} associated files`}
                            aria-label={childrenLoading ? "Loading associated files" : `View ${associatedFileCount} associated files`}
                            disabled={childrenLoading}
                            onClick={() => onOpenChildren(image)}
                        >

                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M3.5 6.5h6l2 2h9v10h-17z" />
                            </svg>

                            <span>{associatedFileCount}</span>

                        </button>

                    }

                </div>

                {/* Open the full-size image or associated file in a new browser tab. */}
                <a
                    href={image.image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="image-card__open-image"
                    title={isImageFile ? "Open full-size image" : "Open file"}
                    aria-label={isImageFile ? "Open full-size image" : "Open file"}
                >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="10.5" cy="10.5" r="6.5" />
                        <path d="M15.5 15.5L21 21" />
                    </svg>
                </a>

            </div>

        </div>

    );

};

export default ImageCard;